import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, get, useWatch } from 'react-hook-form';
import {
  Divider,
  Stack,
  DialogContent,
  DialogActions,
  Dialog,
  Button,
  MenuItem,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { yupResolver } from '@hookform/resolvers/yup';

import { Strings } from '@app/constants';
import { Header } from '@app/sections/header';
import { FormProvider, RHFSelect, RHFTextField, RHFUploadAvatar, Scrollbar } from '@app/components';
import { useRequestState } from '@app/hooks';
import { IAddPortfolioSchema, IPortfolio } from '../types';
import { addPortfolioSchema } from '../validators';
import { addPortfolio, updatePortfolio } from '../slice';
import { selectAddPortfolioState, selectUpdatePortfolioState } from '../selector';

interface Props {
  open: boolean;
  onClose: VoidFunction;
  row?: IPortfolio;
}

const AddPortfolio = ({ open, onClose, row }: Props) => {
  const dispatch = useDispatch();
  const [isImageUploaded, setIsImageUploaded] = useState(false);

  const methods = useForm<IAddPortfolioSchema>({
    resolver: yupResolver(addPortfolioSchema) as any,
    defaultValues: {
      image: get(row, 'image', ''),
      name: get(row, 'name', ''),
      category: get(row, 'category', ''),
      link: get(row, 'link', ''),
      description: get(row, 'description', ''),
    },
  });
  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });
      if (file) {
        methods.setValue('image', newFile, { shouldValidate: true });
        setIsImageUploaded(true);
      }
    },
    [methods]
  );

  const { control } = methods;
  const file = useWatch({ control, name: 'image' });
  const onSubmit = (form: IAddPortfolioSchema) => {
    row
      ? dispatch(updatePortfolio({ ...form, id: row?.id, image: file }))
      : dispatch(addPortfolio({ ...form, image: file }));
  };

  const { loading: savePortfolio } = useRequestState({
    stateSelector: row ? selectUpdatePortfolioState : selectAddPortfolioState,
    onSuccess: onClose,
    successMessageShown: true,
    errorShown: true,
  });
  return (
    <>
      <FormProvider methods={methods}>
        <Dialog open={open} fullWidth>
          <Header
            variant="dialog"
            title={row ? Strings.portfolio.updatePortfolio : Strings.portfolio.addPortfolio}
            onClose={onClose}
          />
          <Divider />
          <Scrollbar>
            <DialogContent>
              <Stack my={3}>
                <RHFUploadAvatar
                  name={'image'}
                  onDrop={handleDrop}
                  sx={{
                    borderRadius: '10px',
                    width: '100%',
                    height: 115,
                    border: 'none',
                  }}
                  placeholderSx={{
                    borderRadius: '10px',
                    width: '100%',
                    height: '100%',
                    border:
                      isImageUploaded || row?.image ? 'none' : '5px dashed rgba(0, 0, 0, 0.08)',
                  }}
                />
              </Stack>
              <Stack my={3} justifyContent={'space-between'} direction={'row'} spacing={2}>
                <RHFTextField
                  required
                  name="name"
                  label={Strings.field.name}
                  fullWidth
                  variant="outlined"
                />
              </Stack>
              <Stack my={2} justifyContent={'space-between'} direction={'row'} spacing={2}>
                <RHFSelect required name="category" label={Strings.field.category} fullWidth>
                  <MenuItem value={'WebApp'}>{'Web App'}</MenuItem>
                  <MenuItem value={'MobileApp'}>{'Mobile App'}</MenuItem>
                  <MenuItem value={'Desktop App'}>{'Desktop App'}</MenuItem>
                </RHFSelect>
                <RHFTextField required label={Strings.field.link} name="link" fullWidth />
              </Stack>
              <Stack my={2} justifyContent={'space-between'} direction={'row'} spacing={2}>
                <RHFTextField
                  required
                  multiline
                  name="description"
                  label={Strings.field.description}
                  variant="outlined"
                  minRows={3}
                  inputProps={{ maxLength: 1000 }}
                />
              </Stack>
            </DialogContent>
          </Scrollbar>
          <Divider sx={{ pt: 2 }} />
          <DialogActions>
            <Button variant="outlined" color="inherit" onClick={onClose}>
              {Strings.button.cancel}
            </Button>
            <LoadingButton
              variant="contained"
              color="primary"
              type="submit"
              loading={savePortfolio}
              onClick={methods.handleSubmit(onSubmit)}
            >
              {Strings.button.save}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </FormProvider>
    </>
  );
};

export { AddPortfolio };
